import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, buildFileUrl, formatEuro } from "@/api";
import { Plus, Edit, Trash2, LogOut, Upload, X } from "lucide-react";
import { toast } from "sonner";

const emptyVeh = {
  titolo: "", marca: "", modello: "", anno: 2024, prezzo: 0, km: 0,
  alimentazione: "Benzina", cambio: "Automatico", cilindrata: 0, potenza: "",
  colore_esterno: "", colore_interno: "", badge: "Disponibile",
  descrizione: "", galleria: [], video_url: null, optionals: [],
};

const AdminDashboard = () => {
  const { admin, loading, logout } = useAuth();
  const [tab, setTab] = useState("vehicles");
  const [vehicles, setVehicles] = useState([]);
  const [leads, setLeads] = useState({ contatti: [], valutazioni: [], testdrives: [] });
  const [editing, setEditing] = useState(null);

  const load = React.useCallback(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/admin/leads").then((r) => setLeads(r.data));
  }, []);
  useEffect(() => { if (admin) load(); }, [admin, load]);

  if (loading) return <div className="p-10 text-zinc-500">Caricamento…</div>;
  if (!admin) return <Navigate to="/admin" replace />;

  const del = async (id) => {
    if (!window.confirm("Eliminare questo veicolo?")) return;
    await api.delete(`/admin/vehicles/${id}`);
    toast.success("Veicolo eliminato"); load();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex justify-between items-center mb-8">
        <div>
          <div className="text-xs tracking-[0.4em] text-[#E1BC56] font-display mb-1">DASHBOARD</div>
          <h1 className="font-display text-3xl font-extrabold text-white tracking-widest">AREA ADMIN</h1>
          <div className="text-xs text-zinc-500 mt-1">Loggato come {admin.email}</div>
        </div>
        <button onClick={logout} className="cmc-btn-outline px-4 py-2 rounded-sm text-xs inline-flex items-center gap-2" data-testid="admin-logout"><LogOut className="w-4 h-4" /> ESCI</button>
      </div>

      <div className="flex gap-2 mb-6 border-b border-white/10">
        {[["vehicles", "Veicoli"], ["leads", `Lead (${leads.contatti.length + leads.valutazioni.length + leads.testdrives.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-5 py-3 font-display text-xs tracking-widest uppercase transition ${tab === k ? "text-[#E1BC56] border-b-2 border-[#E1BC56]" : "text-zinc-500"}`} data-testid={`admin-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "vehicles" && (
        <div>
          <div className="flex justify-between items-center mb-5">
            <div className="text-sm text-zinc-400">{vehicles.length} veicoli in catalogo</div>
            <button onClick={() => setEditing(emptyVeh)} className="cmc-btn-gold px-4 py-2 rounded-sm text-xs inline-flex items-center gap-2" data-testid="admin-new-vehicle"><Plus className="w-4 h-4" /> NUOVO VEICOLO</button>
          </div>
          <div className="bg-[#121214] border border-white/10 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0B0B0C] text-xs text-zinc-500 tracking-widest uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Foto</th>
                  <th className="text-left px-4 py-3">Titolo</th>
                  <th className="text-left px-4 py-3">Anno</th>
                  <th className="text-left px-4 py-3">Km</th>
                  <th className="text-left px-4 py-3">Prezzo</th>
                  <th className="text-left px-4 py-3">Stato</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v) => (
                  <tr key={v.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">{v.galleria?.[0] && <img src={buildFileUrl(v.galleria[0])} className="w-16 h-10 object-cover rounded-sm" />}</td>
                    <td className="px-4 py-3 text-white">{v.titolo}</td>
                    <td className="px-4 py-3 text-zinc-400">{v.anno}</td>
                    <td className="px-4 py-3 text-zinc-400">{v.km.toLocaleString("it-IT")}</td>
                    <td className="px-4 py-3 text-[#E1BC56]">{formatEuro(v.prezzo)}</td>
                    <td className="px-4 py-3"><span className="text-xs">{v.badge}</span></td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setEditing(v)} className="p-2 hover:text-[#E1BC56]" data-testid={`edit-${v.id}`}><Edit className="w-4 h-4" /></button>
                      <button onClick={() => del(v.id)} className="p-2 hover:text-red-400" data-testid={`del-${v.id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "leads" && (
        <div className="space-y-8">
          <LeadSection title="Contatti" items={leads.contatti} renderItem={(l) => `${l.nome} · ${l.email} · ${l.telefono || "—"} — ${l.messaggio}`} />
          <LeadSection title="Valutazioni Usato" items={leads.valutazioni} renderItem={(l) => `${l.nome} ${l.cognome} · ${l.email} — ${l.marca} ${l.modello} ${l.anno} · ${l.km} km${l.prezzo_desiderato ? ` · richiesta € ${l.prezzo_desiderato}` : ""}`} />
          <LeadSection title="Test Drive" items={leads.testdrives} renderItem={(l) => `${l.nome} · ${l.email} — ${l.vehicle_titolo}${l.data_preferita ? ` · ${l.data_preferita}` : ""}`} />
        </div>
      )}

      {editing && <VehicleEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};

const LeadSection = ({ title, items, renderItem }) => (
  <div className="bg-[#121214] border border-white/10 rounded-lg p-5">
    <div className="font-display text-sm font-bold tracking-widest text-[#E1BC56] mb-3">{title.toUpperCase()} ({items.length})</div>
    {items.length === 0 ? <div className="text-zinc-500 text-sm">Nessun lead.</div> : (
      <ul className="space-y-2">
        {items.map((l) => (
          <li key={l.id} className="text-sm text-zinc-300 border-b border-white/5 pb-2">
            <div className="text-xs text-zinc-500">{new Date(l.created_at).toLocaleString("it-IT")}</div>
            {renderItem(l)}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const VehicleEditor = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);

  const uploadFiles = async (files) => {
    const paths = [];
    for (const file of files) {
      try {
        const fd = new FormData(); fd.append("file", file);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(r.data.path);
      } catch { toast.error(`Errore ${file.name}`); }
    }
    return paths;
  };

  const onImgs = async (e) => {
    const paths = await uploadFiles(Array.from(e.target.files));
    setF({ ...f, galleria: [...(f.galleria || []), ...paths] });
  };
  const onVid = async (e) => {
    const f0 = e.target.files[0]; if (!f0) return;
    const [p] = await uploadFiles([f0]);
    setF({ ...f, video_url: p });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f,
        anno: parseInt(f.anno) || 0,
        prezzo: parseFloat(f.prezzo) || 0,
        km: parseInt(f.km) || 0,
        cilindrata: f.cilindrata ? parseInt(f.cilindrata) : null,
        optionals: typeof f.optionals === "string" ? f.optionals.split(",").map((s) => s.trim()).filter(Boolean) : (f.optionals || []),
      };
      if (f.id) await api.put(`/admin/vehicles/${f.id}`, payload);
      else await api.post("/admin/vehicles", payload);
      toast.success("Salvato"); onSaved();
    } catch { toast.error("Errore salvataggio"); }
    setSaving(false);
  };

  const inp = "w-full bg-[#0B0B0C] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:border-[#E1BC56] focus:outline-none";
  const lab = "block text-[10px] tracking-widest uppercase text-zinc-500 mb-1 font-display";
  const set = (k, isNum = false) => (e) => setF({ ...f, [k]: isNum ? e.target.value : e.target.value });
  const optsStr = Array.isArray(f.optionals) ? f.optionals.join(", ") : (f.optionals || "");

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#121214] border border-[#E1BC56]/30 rounded-lg max-w-3xl w-full p-6 my-10" onClick={(e) => e.stopPropagation()} data-testid="vehicle-editor">
        <div className="flex justify-between items-center mb-5">
          <h3 className="font-display text-xl font-bold text-white tracking-widest">{f.id ? "MODIFICA VEICOLO" : "NUOVO VEICOLO"}</h3>
          <button onClick={onClose}><X className="text-zinc-400 hover:text-white" /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><label className={lab}>Titolo *</label><input required className={inp} value={f.titolo} onChange={set("titolo")} data-testid="ve-titolo" /></div>
          <div><label className={lab}>Marca *</label><input className={inp} value={f.marca} onChange={set("marca")} data-testid="ve-marca" /></div>
          <div><label className={lab}>Modello *</label><input className={inp} value={f.modello} onChange={set("modello")} data-testid="ve-modello" /></div>
          <div><label className={lab}>Anno</label><input type="number" className={inp} value={f.anno} onChange={set("anno")} /></div>
          <div><label className={lab}>Prezzo €</label><input type="number" className={inp} value={f.prezzo} onChange={set("prezzo")} /></div>
          <div><label className={lab}>Km</label><input type="number" className={inp} value={f.km} onChange={set("km")} /></div>
          <div><label className={lab}>Cilindrata (cc)</label><input type="number" className={inp} value={f.cilindrata || ""} onChange={set("cilindrata")} /></div>
          <div><label className={lab}>Potenza</label><input className={inp} value={f.potenza || ""} onChange={set("potenza")} /></div>
          <div><label className={lab}>Alimentazione</label>
            <select className={inp} value={f.alimentazione} onChange={set("alimentazione")}><option>Benzina</option><option>Diesel</option><option>Ibrida</option><option>Elettrica</option></select>
          </div>
          <div><label className={lab}>Cambio</label>
            <select className={inp} value={f.cambio} onChange={set("cambio")}><option>Automatico</option><option>Manuale</option></select>
          </div>
          <div><label className={lab}>Badge Stato</label>
            <select className={inp} value={f.badge} onChange={set("badge")} data-testid="ve-badge"><option>Disponibile</option><option>In Arrivo</option><option>Venduta</option></select>
          </div>
          <div><label className={lab}>Colore Esterno</label><input className={inp} value={f.colore_esterno || ""} onChange={set("colore_esterno")} /></div>
          <div><label className={lab}>Colore Interno</label><input className={inp} value={f.colore_interno || ""} onChange={set("colore_interno")} /></div>
          <div className="md:col-span-2"><label className={lab}>Descrizione</label><textarea rows={4} className={inp} value={f.descrizione} onChange={set("descrizione")} /></div>
          <div className="md:col-span-2"><label className={lab}>Optionals (separati da virgola)</label><input className={inp} value={optsStr} onChange={(e) => setF({ ...f, optionals: e.target.value })} /></div>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="border-2 border-dashed border-white/10 rounded-lg p-4 flex items-center gap-2 cursor-pointer hover:border-[#E1BC56]/50 text-sm text-zinc-300">
            <Upload className="w-4 h-4 text-[#E1BC56]" /> Aggiungi Foto ({f.galleria?.length || 0})
            <input type="file" accept="image/*" multiple className="hidden" onChange={onImgs} />
          </label>
          <label className="border-2 border-dashed border-white/10 rounded-lg p-4 flex items-center gap-2 cursor-pointer hover:border-[#E1BC56]/50 text-sm text-zinc-300">
            <Upload className="w-4 h-4 text-[#E1BC56]" /> {f.video_url ? "Video caricato ✓" : "Video (opzionale)"}
            <input type="file" accept="video/*" className="hidden" onChange={onVid} />
          </label>
        </div>
        {f.galleria?.length > 0 && (
          <div className="grid grid-cols-4 md:grid-cols-6 gap-2 mt-3">
            {f.galleria.map((g, i) => (
              <div key={i} className="relative">
                <img src={buildFileUrl(g)} className="w-full h-16 object-cover rounded-sm border border-white/10" />
                <button onClick={() => setF({ ...f, galleria: f.galleria.filter((_, k) => k !== i) })} className="absolute -top-1 -right-1 bg-red-600 rounded-full w-4 h-4 flex items-center justify-center text-white"><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 flex gap-3">
          <button onClick={save} disabled={saving} className="cmc-btn-gold px-6 py-3 rounded-sm flex-1" data-testid="ve-save">{saving ? "SALVATAGGIO..." : "SALVA"}</button>
          <button onClick={onClose} className="cmc-btn-outline px-6 py-3 rounded-sm">ANNULLA</button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
